import React, { useMemo, useState } from 'react';
import {
  AreaChart,
  Area,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine
} from 'recharts';
import Icon from '../../../components/AppIcon';
import Select from '../../../components/ui/Select';
import { buildProjectionData } from '../../../services/planningProjection';

const KPI_OPTIONS = [
  { value: 'value', label: 'Valeur du portefeuille', dataKey: 'value' },
  { value: 'equity', label: 'Équité', dataKey: 'equity' },
  { value: 'cashflow', label: 'Cashflow annuel', dataKey: 'cashflow' },
  { value: 'revenue', label: 'Revenus annuels', dataKey: 'revenue' }
];

const PlanningProjectionChart = ({ properties = [], projects = [], formatCurrency }) => {
  const [selectedKpi, setSelectedKpi] = useState('value');

  const { chartData, projectEvents } = useMemo(() => {
    return buildProjectionData(properties, projects);
  }, [properties, projects]);

  const selectedOption = KPI_OPTIONS.find((o) => o.value === selectedKpi) || KPI_OPTIONS[0];
  const dataKey = selectedOption.dataKey;

  const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    const point = chartData.find((d) => d.year === Number(label)) ?? chartData[Number(label)];
    const value = payload[0]?.value;
    return (
      <div className="bg-card border border-border rounded-lg shadow-lg p-3 text-sm min-w-[180px]">
        <p className="font-semibold text-foreground mb-2">
          Année {point?.yearLabel ?? label}
        </p>
        <p className="text-muted-foreground mb-1">
          {selectedOption.label}: <span className="font-semibold text-foreground">{formatCurrency(value)}</span>
        </p>
        {point?.events?.length > 0 && (
          <div className="mt-2 pt-2 border-t border-border">
            <p className="text-xs font-medium text-muted-foreground mb-1">Impact des projets :</p>
            <ul className="text-xs text-foreground space-y-0.5">
              {point.events.map((name, i) => (
                <li key={i}>• {name}</li>
              ))}
            </ul>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="bg-card border border-border rounded-xl p-5 md:p-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4">
        <div>
          <h2 className="text-lg font-semibold text-foreground flex items-center gap-2">
            <Icon name="TrendingUp" size={20} className="text-primary" />
            Projection sur 30 ans
          </h2>
          <p className="text-sm text-muted-foreground mt-1">
            Évolution du portefeuille avec les projets planifiés ou terminés
          </p>
        </div>
        <div className="w-full sm:w-56">
          <label className="block text-sm font-medium text-foreground mb-2">KPI affiché</label>
          <Select
            options={KPI_OPTIONS.map((o) => ({ value: o.value, label: o.label }))}
            value={selectedKpi}
            onChange={(v) => setSelectedKpi(v || 'value')}
            placeholder="Choisir un KPI"
          />
        </div>
      </div>

      <div className="w-full h-64 md:h-80 lg:h-96 mt-4">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={chartData} margin={{ top: 10, right: 10, left: 10, bottom: 5 }}>
            <defs>
              <linearGradient id="colorProjection" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="var(--color-primary)" stopOpacity={0.3} />
                <stop offset="95%" stopColor="var(--color-primary)" stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" vertical={false} />
            <XAxis
              dataKey="year"
              stroke="var(--color-muted-foreground)"
              style={{ fontSize: '0.75rem', fontFamily: 'var(--font-caption)' }}
              tickFormatter={(v) => (v === 0 ? 'Auj.' : `+${v}`)}
              label={{ value: "Années (à partir d'aujourd'hui)", position: 'insideBottom', offset: -5, style: { fontSize: '0.75rem' } }}
            />
            <YAxis
              stroke="var(--color-muted-foreground)"
              style={{ fontSize: '0.75rem', fontFamily: 'var(--font-caption)' }}
              tickFormatter={(v) => (v >= 1e6 ? `${(v / 1e6).toFixed(1)} M` : v >= 1e3 ? `${(v / 1e3).toFixed(0)} k` : String(Math.round(v)))}
              width={48}
            />
            <Tooltip content={<CustomTooltip />} />
            {projectEvents.map((evt) => (
              <ReferenceLine
                key={`${evt.projectId}-${evt.year}`}
                x={evt.year}
                stroke="var(--color-muted-foreground)"
                strokeDasharray="2 2"
                strokeOpacity={0.6}
              />
            ))}
            <Area
              type="monotone"
              dataKey={dataKey}
              name={selectedOption.label}
              stroke="var(--color-primary)"
              strokeWidth={2.5}
              fill="url(#colorProjection)"
              fillOpacity={1}
              dot={false}
              activeDot={{ r: 5, strokeWidth: 2 }}
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>

      {projectEvents.length > 0 && (
        <div className="mt-4 pt-4 border-t border-border">
          <p className="text-sm font-medium text-foreground mb-2">Impact sur la courbe</p>
          <p className="text-xs text-muted-foreground mb-2">
            Les traits pointillés indiquent l&apos;année où chaque projet a un effet sur le KPI.
          </p>
          <ul className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-foreground">
            {projectEvents.map((evt, i) => (
              <li key={`${evt.projectId}-${evt.year}-${i}`} className="flex items-center gap-2">
                <span className="inline-block w-2 h-2 rounded-full bg-primary shrink-0" />
                <span className="font-medium">{evt.projectName}</span>
                <span className="text-muted-foreground">→ année {evt.yearLabel}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default PlanningProjectionChart;
